/** Shared state behind every schedules list on screen: a short-lived read
 * cache scoped to the signed-in account, and change notification so the
 * settings panel, the QuantisAI section and the admin list never disagree.
 * Changes travel as a `bot-schedules:changed` window event, the project's
 * page bridge convention, so any list can join without importing the others. */
import type { BotScheduleList } from '../../../services/BotTaskSchedulesService';

export const BOT_SCHEDULES_CHANGED_EVENT = 'bot-schedules:changed';
export interface BotSchedulesChangedDetail { workspaceId: string; origin: string }

/** Reopening a panel shows what it had instead of refetching. */
const TTL_MS = 30_000;
const cache = new Map<string, { at: number; list: BotScheduleList; workspaceId: string }>();
/** Bumped on every change to a workspace: a list request that started before
 * it is stale and must not overwrite the newer row or reach the cache. */
const versions = new Map<string, number>();

/** The signed-in account, read from the stored session user so this module
 * needs no auth client or provider. */
export function currentScheduleActor(): string {
  try {
    const stored = typeof localStorage !== 'undefined' ? localStorage.getItem('auth_user') : null;
    const id = stored ? (JSON.parse(stored) as { id?: unknown }).id : null;
    return typeof id === 'string' && id ? id : 'anon';
  } catch { return 'anon'; }
}

// Sign-out clears the app's per-user caches with this event; do the same.
if (typeof window !== 'undefined') window.addEventListener('quantis:auth-cleared', () => cache.clear());

export const scheduleCacheKey = (workspaceId: string, scope: string, botId?: string) =>
  `${currentScheduleActor()}|${workspaceId}|${scope}|${botId ?? ''}`;

export function readScheduleCache(key: string): BotScheduleList | null {
  const hit = cache.get(key);
  return hit && Date.now() - hit.at < TTL_MS ? hit.list : null;
}
export const writeScheduleCache = (key: string, workspaceId: string, list: BotScheduleList) => { cache.set(key, { at: Date.now(), list, workspaceId }); };
export const scheduleVersion = (workspaceId: string) => versions.get(workspaceId) ?? 0;
export const clearBotSchedulesCache = () => { cache.clear(); versions.clear(); };

/** Call after any create, edit, pause, resume or delete. */
export function announceSchedulesChanged(workspaceId: string, origin: string): void {
  versions.set(workspaceId, scheduleVersion(workspaceId) + 1);
  for (const [key, entry] of cache) if (entry.workspaceId === workspaceId) cache.delete(key);
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent<BotSchedulesChangedDetail>(BOT_SCHEDULES_CHANGED_EVENT, { detail: { workspaceId, origin } }));
}
